import React, { useMemo, useState, useRef, useEffect } from "react";
import { Fade, Typography, Box, Skeleton } from "@mui/material";
import { TableContainer as StyledTableContainer } from "./styles";
import { MaterialReactTable } from "material-react-table";
import ReactEcharts from "echarts-for-react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

function TableView({
  dataLoading,
  summaryData,
  summaryColumns,
  currentChartOption,
  onChartReady,
  itemsData,
  selectedItems,
  displayDates,
}) {
  const [tableKey, setTableKey] = useState(0);
  const chartScrollRef = useRef(null);
  const tableContainerRef = useRef(null);
  const isSyncingRef = useRef(false);
  const groupNameDisplay = useMemo(() => {
    if (!selectedItems || !itemsData || selectedItems.length === 0)
      return "요약";
    const groupsMap = {};
    selectedItems.forEach((itemId) => {
      const item = itemsData.find((i) => i.id.toString() === itemId);
      if (item) {
        const group = item.materialGroup || "기타";
        if (!groupsMap[group]) groupsMap[group] = [];
        groupsMap[group].push(item.name);
      }
    });
    const groupTexts = Object.entries(groupsMap).map(([group, names]) => {
      if (names.length > 2) {
        return (
          <span key={group}>
            <strong>{group}</strong>({names[0]}, {names[1]} 외{" "}
            {names.length - 2}개)
          </span>
        );
      }
      return (
        <span key={group}>
          <strong>{group}</strong>({names.join(", ")})
        </span>
      );
    });
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {groupTexts.reduce((acc, elem, i) => {
          if (i > 0) acc.push(<span key={`sep-${i}`}>, </span>);
          acc.push(elem);
          return acc;
        }, [])}
      </Box>
    );
  }, [selectedItems, itemsData]);
  const handlePanelResize = () => {
    setTableKey((prev) => prev + 1);
  };
  const legendItems = [
    { name: "생산계획", color: "#1890ff" },
    { name: "입고예정", color: "#52c41a" },
    { name: "출고예정", color: "#fa8c16" },
    { name: "예상재고", color: "#13c2c2", isDashed: false },
    { name: "안전재고", color: "#f5222d", isDashed: true },
  ];
  const chartWidth = useMemo(() => {
    return Math.max(displayDates.length * 103, 800);
  }, [displayDates]);
  useEffect(() => {
    const findTableContainer = () => {
      return tableContainerRef.current?.querySelector(".MuiTable-root")
        ?.parentElement;
    };
    const tableContainer = findTableContainer();
    const chartContainer = chartScrollRef.current;
    if (!tableContainer || !chartContainer) {
      const timer = setTimeout(() => {
        const retryTableContainer = findTableContainer();
        if (retryTableContainer && chartContainer) {
          setupScrollSync(retryTableContainer, chartContainer);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    setupScrollSync(tableContainer, chartContainer);
    function setupScrollSync(table, chart) {
      const handleTableScroll = () => {
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        requestAnimationFrame(() => {
          if (chart.scrollLeft !== table.scrollLeft) {
            chart.scrollLeft = table.scrollLeft;
          }
          isSyncingRef.current = false;
        });
      };
      const handleChartScroll = () => {
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        requestAnimationFrame(() => {
          if (table.scrollLeft !== chart.scrollLeft) {
            table.scrollLeft = chart.scrollLeft;
          }
          isSyncingRef.current = false;
        });
      };
      table.addEventListener("scroll", handleTableScroll);
      chart.addEventListener("scroll", handleChartScroll);
      return () => {
        table.removeEventListener("scroll", handleTableScroll);
        chart.removeEventListener("scroll", handleChartScroll);
      };
    }
  }, [tableKey, dataLoading]);
  return (
    <PanelGroup direction="vertical" style={{ height: "100%", width: "100%" }}>
      <Panel defaultSize={50} minSize={20} onResize={handlePanelResize}>
        <Fade in={!dataLoading} timeout={500}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <StyledTableContainer sx={{ flex: "0 0 auto" }}>
              <Typography variant="h6" gutterBottom sx={{ px: 2, pt: 2 }}>
                {groupNameDisplay}
              </Typography>
            </StyledTableContainer>
            {dataLoading ? (
              <Box sx={{ p: 2 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={200}
                  animation="wave"
                />
              </Box>
            ) : (
              <Box
                ref={tableContainerRef}
                sx={{ flex: "1 1 auto", overflow: "hidden" }}
              >
                <MaterialReactTable
                  key={tableKey}
                  columns={summaryColumns}
                  data={summaryData}
                  enableTopToolbar={false}
                  enableColumnActions={false}
                  enableColumnPinning
                  enableColumnFilters={false}
                  enablePagination={false}
                  enableSorting={false}
                  enableBottomToolbar={false}
                  enableRowSelection={false}
                  initialState={{ columnPinning: { left: ["type"] } }}
                  muiTableProps={{ sx: { tableLayout: "fixed" } }}
                  muiTableHeadCellProps={{
                    sx: { textAlign: "center", backgroundColor: "#f5f5f5" },
                  }}
                  muiTableHeadRowProps={{
                    sx: { "& > th": { borderRight: "1px solid #E0DEE3" } },
                  }}
                  muiTableBodyRowProps={{
                    sx: {
                      height: "40px !important",
                      maxHeight: "40px !important",
                      "& > td": {
                        height: "40px !important",
                        padding: "0px 16px",
                        borderRight: "1px solid #E0DEE3",
                      },
                      "&:hover": { backgroundColor: "#f5f5f5 !important" },
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      height: "40px !important",
                      padding: "0px 16px !important",
                      verticalAlign: "middle",
                      textAlign: "center",
                    },
                  }}
                  muiTableContainerProps={{
                    sx: {
                      height: "100%",
                      maxHeight: "100%",
                      overflow: "auto",
                      "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(99, 96, 150, 0.7)",
                        borderRadius: "5px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Fade>
      </Panel>
      <PanelResizeHandle
        style={{
          position: "relative",
          height: 12,
          background: "transparent",
          cursor: "row-resize",
        }}
      >
        <Box
          sx={{
            mx: "auto",
            mt: "2px",
            width: 56,
            height: 6,
            borderRadius: 9999,
            bgcolor: "#E5E7EB",
            border: "1px solid #E5E7EB",
            transition: "all .15s ease",
            "&:hover": { bgcolor: "#D1D5DB", borderColor: "#D1D5DB" },
            "&:active": { bgcolor: "#9CA3AF", borderColor: "#9CA3AF" },
          }}
        />
      </PanelResizeHandle>
      <Panel defaultSize={50} minSize={20}>
        <Fade in={!dataLoading} timeout={700}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="h6">아이템 분석</Typography>
            </Box>
            {dataLoading ? (
              <Box
                sx={{
                  flex: "1 1 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  pb: 2,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  animation="wave"
                />
              </Box>
            ) : (
              <Box
                ref={chartScrollRef}
                sx={{
                  flex: "1 1 auto",
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "100%",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <ReactEcharts
                  option={currentChartOption}
                  style={{
                    height: "100%",
                    width: `${chartWidth}px`,
                    minWidth: "100%",
                    marginLeft: "0px",
                  }}
                  opts={{ renderer: "canvas" }}
                  onChartReady={onChartReady}
                  notMerge
                  lazyUpdate={false}
                />
              </Box>
            )}
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: "1rem" }}
            >
              {legendItems.map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: item.isDashed ? 20 : 14,
                      height: item.isDashed ? 0 : 14,
                      backgroundColor: item.isDashed
                        ? "transparent"
                        : item.color,
                      borderTop: item.isDashed
                        ? `2px dashed ${item.color}`
                        : "none",
                      borderRadius: item.isDashed ? 0 : 1,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>
                    {item.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Fade>
      </Panel>
    </PanelGroup>
  );
}

export default TableView;
